import React, { useState } from 'react';
import {
  DndContext, DragEndEvent, DragOverEvent, DragStartEvent,
  PointerSensor, useSensor, useSensors, DragOverlay, closestCorners,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Task, TaskStatus } from '@shared/types';
import { TaskCard } from '@shared/components/tasks/TaskCard';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Plus } from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { useCreateTaskMutation, useUpdateTaskMutation } from '@shared/hooks/useTasksQuery';

const COLUMNS: { id: TaskStatus; label: string; color: string }[] = [
  { id: 'TODO', label: 'To-do', color: 'bg-slate-400' },
  { id: 'IN_PROGRESS', label: 'In progress', color: 'bg-blue-500' },
  { id: 'IN_REVIEW', label: 'In review', color: 'bg-yellow-500' },
  { id: 'DONE', label: 'Done', color: 'bg-green-500' },
];

function SortableTaskCard({ task, onClick }: { task: Task; onClick: (t: Task) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.task_id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <TaskCard task={task} onClick={onClick} dragHandleProps={listeners} isDragging={isDragging} />
    </div>
  );
}

interface KanbanColumnProps {
  id: TaskStatus;
  label: string;
  color: string;
  tasks: Task[];
  onCardClick: (task: Task) => void;
}

function KanbanColumn({ id, label, color, tasks, onCardClick }: KanbanColumnProps) {
  const createTask = useCreateTaskMutation();
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const handleAdd = async () => {
    const title = newTitle.trim();
    if (!title) return;
    await createTask.mutateAsync({ title, status: id });
    setNewTitle('');
    setAdding(false);
  };

  return (
    <div className="flex flex-col flex-1 min-w-[240px] bg-accent/30 rounded-xl p-3 gap-2">
      {/* Column header */}
      <div className="flex items-center justify-between mb-1 px-1">
        <div className="flex items-center gap-2">
          <span className={cn('h-2.5 w-2.5 rounded-full', color)} />
          <span className="text-sm font-semibold">{label}</span>
          <span className="text-xs text-muted-foreground bg-accent rounded-full px-1.5">{tasks.length}</span>
        </div>
        <Button
          variant="ghost" size="icon" className="h-6 w-6"
          onClick={() => setAdding(true)}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Cards */}
      <SortableContext items={tasks.map(t => t.task_id)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-2 min-h-[40px]">
          {tasks.map(task => (
            <SortableTaskCard key={task.task_id} task={task} onClick={onCardClick} />
          ))}
        </div>
      </SortableContext>

      {/* Add card */}
      {adding ? (
        <div className="flex flex-col gap-1.5 mt-1">
          <Input
            autoFocus
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            placeholder="Task title..."
            className="h-8 text-sm"
            onKeyDown={e => {
              if (e.key === 'Enter') handleAdd();
              if (e.key === 'Escape') { setAdding(false); setNewTitle(''); }
            }}
          />
          <div className="flex gap-1">
            <Button size="sm" className="h-7 text-xs flex-1" onClick={handleAdd} disabled={!newTitle.trim() || createTask.isPending}>
              Add
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setAdding(false); setNewTitle(''); }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <button
          className="flex items-center gap-1.5 text-xs text-muted-foreground/60 hover:text-muted-foreground px-1 py-1 rounded-md hover:bg-accent/50 transition-colors mt-1"
          onClick={() => setAdding(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          Add task
        </button>
      )}
    </div>
  );
}

interface KanbanBoardProps {
  tasks: Task[];
  onCardClick: (task: Task) => void;
}

export function KanbanBoard({ tasks, onCardClick }: KanbanBoardProps) {
  const updateTask = useUpdateTaskMutation();
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const columnTasks = (status: TaskStatus) =>
    tasks.filter(t => t.status === status).sort((a, b) => a.sequence - b.sequence);

  const handleDragStart = (e: DragStartEvent) => {
    setActiveTask(tasks.find(t => t.task_id === e.active.id) ?? null);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveTask(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;

    // Determine target column: over could be a column id or another task id
    const targetStatus = COLUMNS.find(c => c.id === over.id)?.id
      ?? tasks.find(t => t.task_id === over.id)?.status;

    if (targetStatus && targetStatus !== tasks.find(t => t.task_id === active.id)?.status) {
      updateTask.mutate({ taskId: active.id as string, payload: { status: targetStatus } });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 px-6">
        {COLUMNS.map(col => (
          <KanbanColumn
            key={col.id}
            {...col}
            tasks={columnTasks(col.id)}
            onCardClick={onCardClick}
          />
        ))}
      </div>

      <DragOverlay>
        {activeTask && (
          <div className="rotate-2 opacity-90">
            <TaskCard task={activeTask} onClick={() => {}} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
